<template>
  <div v-if="isEnabled && showInsight" class="insight-summary-overlay">
    <div class="insight-card-premium">
      <div class="insight-header-premium">
        <div class="title-with-icon">
          <div class="ai-spark-icon">
            <i class="el-icon-magic-stick"></i>
          </div>
          <div>
            <h5>AI Health Insight</h5>
            <small>{{ chartData ? chartData.title : 'Data Summary' }}</small>
          </div>
        </div>
        <button @click="closeInsight" class="close-premium-btn">&times;</button>
      </div>

      <div class="insight-body-premium" v-loading="loading" element-loading-background="rgba(255, 255, 255, 0.8)">
        <div v-if="error" class="error-premium">
          <i class="el-icon-warning-outline"></i>
          <p>{{ error }}</p>
          <button @click="generateInsight(chartData)" class="retry-btn">Retry Analysis</button>
        </div>
        <div v-else-if="insight" class="insight-content-rich" v-html="formattedInsight">
        </div>
        <div v-else-if="loading" class="analysis-loading">
          <div class="typing-indicator">
            <span></span><span></span><span></span>
          </div>
          <p>Analyzing health trends...</p>
        </div>
      </div>

      <div class="insight-footer-premium">
        <div class="ai-badge">
          <span class="pulse-dot"></span> Powered by MSDAT Analytics AI
        </div>
        <button class="feedback-mini-btn" title="Was this helpful?">
          <i class="el-icon-finished"></i> Helpful
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { marked } from 'marked';

export default {
  name: 'InsightSummaryPlugin',
  data() {
    return {
      isEnabled: true,
      showInsight: false,
      loading: false,
      insight: '',
      error: '',
      chartData: null,
    };
  },
  computed: {
    formattedInsight() {
      if (!this.insight) return '';
      // Simple sanitization and markdown conversion
      return marked(this.insight);
    },
  },
  methods: {
    onPluginDisable() {
      this.isEnabled = false;
      this.showInsight = false;
    },
    onPluginEnable() {
      this.isEnabled = true;
    },
    closeInsight() {
      this.showInsight = false;
    },
    async generateInsight(payload) {
      if (!payload) return;
      this.showInsight = true;
      this.loading = true;
      this.error = '';
      this.insight = '';
      this.chartData = payload;

      try {
        const GPT_URL = process.env.VUE_APP_API_GPT_URL || 'https://e4egpt.e4eweb.space/';

        const gptClient = axios.create({
          baseURL: GPT_URL,
          timeout: 60000,
        });

        // Exact match of PolicyService configuration
        const gptConfig = {
          temperature: 1,
          max_new_tokens: 1500,
          min_new_tokens: -1,
        };

        const systemPrompt = `You are a Senior Public Health Storyteller. 
        Your goal is to explain health data to a non-technical community leader.
        - **Tone**: Warm, clear, and professional. Avoid "data" talk; use "community" talk.
        - **Format**:
          1. **The Big Story**: One headline that tells the reader what's happening (e.g., "Maternal health is improving in your region").
          2. **The "Why"**: Explain the numbers using an analogy (e.g., "This is like ensuring every child has a school uniform").
          3. **Impact**: What does this mean for the person on the street?
          4. **Next Step**: One simple, non-technical recommendation.
        - **Rule**: Never use the words "Minimum," "Maximum," or "Dataset." Use "Starting point," "Peak," and "Information" instead.`;

        const dataStr = (payload.series || []).map((s) => {
          const vals = (s.data || []).map((v) => (typeof v === 'string' ? parseFloat(v) : v)).filter((v) => !Number.isNaN(v));
          const current = vals.length ? vals[vals.length - 1] : 0;
          const peak = vals.length ? Math.max(...vals) : 0;
          const start = vals.length ? vals[0] : 0;
          return `- For ${s.name}: It started at ${start}, reached a high of ${peak}, and is now at ${current}.`;
        }).join('\n');

        const categoriesStr = (payload.categories && Array.isArray(payload.categories))
          ? payload.categories.join(', ')
          : 'Timeline';

        const prompt = `INDICATOR BRIEFING REQUEST:
        CHART: ${payload.title}
        AREAS: ${categoriesStr}
        RAW DATA SYNOPSIS:
        ${dataStr}
        
        Please generate the high-level non-technical briefing.`;

        const response = await gptClient.post('e4e/ask/', {
          text: prompt,
          ...gptConfig,
          system_prompt: systemPrompt,
        });

        if (response.data && response.data.response) {
          this.insight = response.data.response;
        } else {
          throw new Error('Our AI is currently processing other requests. Please try again.');
        }
      } catch (err) {
        console.error('Insight generation error:', err);

        let customError = '';
        // Special check for Replicate billing error in the 500 response
        if (err.response && err.response.data && err.response.data.detail) {
          const detail = err.response.data.detail;
          if (detail.includes('billing') || detail.includes('replicate')) {
            customError = 'MSDAT AI Service Alert: The backend AI engine (Replicate) requires a billing update. Please contact the administrator to top up the account credits.';
          } else {
            customError = `AI Service Error: ${detail}`;
          }
        }

        if (customError) {
          console.warn('AI Billing/Request error, falling back to local simulation:', customError);
          this.generateSimulatedInsight(payload);
          return;
        } if (err.code === 'ECONNABORTED') {
          this.error = 'The AI is taking longer than usual. Please try again in a moment.';
        } else if (!navigator.onLine) {
          this.error = 'No internet connection detected. Please check your network.';
        } else {
          console.warn('Internal server error, falling back to local simulation');
          this.generateSimulatedInsight(payload);
          return;
        }
      } finally {
        this.loading = false;
      }
    },
    generateSimulatedInsight(payload) {
      const categoriesCount = (payload.categories && Array.isArray(payload.categories)) ? payload.categories.length : 'multiple';

      this.insight = `### 📖 The Story of Your Data: ${payload.title}
  
  **The Big Picture:**
  Looking across **${categoriesCount}** different areas, we are seeing a journey that tells us a lot about our community's wellbeing. Some areas are thriving, while others are still waiting for their turn to improve.
  
  **What the Numbers Are Telling Us:**
  ${payload.series.map((s) => {
    const rawData = (s.data || []).map((v) => (typeof v === 'string' ? parseFloat(v) : v));
    const firstVal = rawData[0] || 0;
    const current = rawData.length ? rawData[rawData.length - 1] : 0;
    const trend = current > firstVal ? 'upward' : 'downward or stagnant';

    return `#### 📍 ${s.name}
  - **Where we are now**: **${current.toFixed(1)}**
  - **The Trend**: We've seen a ${trend} move since we started recording this.
  
  Think of this like a **community garden**: specialized care (funding and focus) is helping ${s.name} grow, but we need to keep watering it (consistent support) to stay at this level.`;
  }).join('\n')}
  
  **🌟 Why This Matters to You:**
  When these numbers go up, it means fewer families are worried at night. It means the systems we've built are finally reaching the people who need them most. 
  
  **✅ One Thing You Can Do Today:**
  Identify the highest-performing areas and ask the teams there what their "secret sauce" is, so we can replicate that success everywhere else.
  
  *⚠️ Note: This is an automatically generated data story based on live chart values. Advanced AI narratives are currently in read-only mode.*`;
    },
  },
  mounted() {
    this.$eventBus.$on('show-insight', this.generateInsight);
  },
  beforeDestroy() {
    this.$eventBus.$off('show-insight', this.generateInsight);
  },
};
</script>

<style scoped>
.insight-summary-overlay {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 420px;
  z-index: 10000;
  font-family: 'Work Sans', sans-serif;
  animation: slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes slideUp {
  from { transform: translateY(100px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.insight-card-premium {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 15px 50px rgba(0, 50, 30, 0.2);
  border: 1px solid rgba(0, 125, 83, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 600px;
}

.insight-header-premium {
  padding: 20px 24px;
  background: linear-gradient(135deg, #007d53 0%, #004d33 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-with-icon {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ai-spark-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.insight-header-premium h5 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.insight-header-premium small {
  opacity: 0.8;
  font-size: 12px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
}

.close-premium-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  line-height: 1;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.close-premium-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.insight-body-premium {
  padding: 24px;
  overflow-y: auto;
  min-height: 150px;
}

.insight-content-rich {
  font-size: 15px;
  line-height: 1.6;
  color: #2c3e50;
}

.insight-content-rich >>> h1, .insight-content-rich >>> h2, .insight-content-rich >>> h3 {
  font-size: 17px;
  color: #007d53;
  margin-top: 15px;
  margin-bottom: 8px;
}

.insight-content-rich >>> p {
  margin-bottom: 12px;
}

.insight-content-rich >>> ul {
  padding-left: 20px;
  margin-bottom: 15px;
}

.analysis-loading {
  text-align: center;
  padding: 40px 0;
}

.typing-indicator {
  display: flex;
  justify-content: center;
  gap: 5px;
  margin-bottom: 10px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #007d53;
  border-radius: 50%;
  animation: wave 1.3s infinite;
}

.typing-indicator span:nth-child(2) { animation-delay: 0.15s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.3s; }

@keyframes wave {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-10px); }
}

.error-premium {
  text-align: center;
  color: #e74c3c;
  padding: 20px;
}

.error-premium i {
  font-size: 40px;
  margin-bottom: 15px;
}

.retry-btn {
  background: #fdfdfd;
  border: 1px solid #e74c3c;
  color: #e74c3c;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 10px;
  transition: all 0.2s;
}

.retry-btn:hover {
  background: #e74c3c;
  color: white;
}

.insight-footer-premium {
  padding: 15px 24px;
  background: #f8f9fa;
  border-top: 1px solid #edf2f7;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ai-badge {
  font-size: 11px;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.pulse-dot {
  width: 6px;
  height: 6px;
  background: #2ecc71;
  border-radius: 50%;
  box-shadow: 0 0 0 rgba(46, 204, 113, 0.4);
  animation: pulse-green 2s infinite;
}

@keyframes pulse-green {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(46, 204, 113, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(46, 204, 113, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(46, 204, 113, 0); }
}

.feedback-mini-btn {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.feedback-mini-btn:hover {
  background: #ebf8f1;
  border-color: #c6f6d5;
  color: #276749;
}

/* Scrollbar styling */
.insight-body-premium::-webkit-scrollbar { width: 5px; }
.insight-body-premium::-webkit-scrollbar-track { background: #f1f1f1; }
.insight-body-premium::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
.insight-body-premium::-webkit-scrollbar-thumb:hover { background: #a0aec0; }
</style>
